import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { tagsApi } from '~/entities/tag/api/tags-api'
import { formatTagLabel } from '~/entities/tag/lib/tag-label'
import * as m from '~/paraglide/messages'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Label } from '~/shared/ui/label'
import { Skeleton } from '~/shared/ui/skeleton'

interface AmenitySelectorProps {
  selectedAmenityIds?: string[]
  onAmenitiesChange: (amenityIds: string[]) => void
  /** Shows only this many unselected amenities until the list is expanded. */
  collapsedLimit?: number
}

export function AmenitySelector({
  selectedAmenityIds = [],
  onAmenitiesChange,
  collapsedLimit,
}: AmenitySelectorProps) {
  const [expanded, setExpanded] = useState(false)
  const { data: allTags = [], isLoading } = useQuery({
    queryKey: queryKey(QueryKey.TAGS),
    queryFn: () => tagsApi.getAll(),
  })

  const amenities = useMemo(() => allTags.filter((tag) => tag.tagType === 'amenity'), [allTags])

  const handleToggle = (amenityId: string) => {
    if (selectedAmenityIds.includes(amenityId)) {
      onAmenitiesChange(selectedAmenityIds.filter((id) => id !== amenityId))
    } else {
      onAmenitiesChange([...selectedAmenityIds, amenityId])
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-28" />
          <Skeleton className="h-6 w-22" />
        </div>
      </div>
    )
  }

  if (amenities.length === 0) return null

  const collapsible = collapsedLimit !== undefined && amenities.length > collapsedLimit
  const visible =
    collapsible && !expanded
      ? amenities.filter(
          (amenity, index) => index < collapsedLimit || selectedAmenityIds.includes(amenity.id),
        )
      : amenities
  const hiddenCount = amenities.length - visible.length

  return (
    <div className="space-y-2">
      <Label>{m.amenities()}</Label>
      <div className="flex flex-wrap gap-2">
        {visible.map((amenity) => {
          const isSelected = selectedAmenityIds.includes(amenity.id)
          return (
            <Badge
              key={amenity.id}
              variant={isSelected ? 'default' : 'outline'}
              className={`cursor-pointer transition-colors ${
                isSelected
                  ? 'hover:bg-primary/80'
                  : 'hover:bg-secondary hover:text-secondary-foreground'
              }`}
              onClick={() => handleToggle(amenity.id)}
            >
              {formatTagLabel(amenity.name, 'amenities')}
            </Badge>
          )
        })}
        {collapsible && (hiddenCount > 0 || expanded) && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto px-1 py-0 text-xs"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? m.showLess() : m.amenitiesShowMore({ count: hiddenCount })}
          </Button>
        )}
      </div>
    </div>
  )
}
